"use client";

/**
 * Animated Who-It's-For card. Replaces the older string-emoji approach
 * with a lucide icon + framer-motion-driven hover choreography.
 *
 * Animation language (per DESIGN-LANGUAGE.md):
 *   - Restrained, not consumer-AI flashy.
 *   - Icon: gentle scale + rotate, sage to gold color shift.
 *   - Card: subtle gold ring fade-in, small lift via translateY.
 *   - Honors `prefers-reduced-motion`: drops the rotate and scale,
 *     keeps only the color/ring shifts.
 *
 * Variants are declared on the parent and inherited by children, so the
 * hover state coordinates across the whole card from one `whileHover`.
 */
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";

interface SegmentCardProps {
  Icon: LucideIcon;
  title: string;
  pitch: string;
  cta: string;
  href: string;
  highlight?: boolean;
  highlightLabel?: string;
  onCtaClick?: () => void;
}

export default function SegmentCard({
  Icon,
  title,
  pitch,
  cta,
  href,
  highlight,
  highlightLabel = "Built for retakers",
  onCtaClick,
}: SegmentCardProps) {
  const reduce = useReducedMotion();

  // Card-level variants. The outer ring uses inset-0 absolute so a
  // border crisp-up doesn't reflow the card.
  const cardVariants = {
    rest: { y: 0 },
    hover: { y: reduce ? 0 : -3 },
  };

  const ringVariants = {
    rest: { opacity: 0 },
    hover: { opacity: 1 },
  };

  // The icon container: tints from sage to gold, with a small scale +
  // rotate on hover. Spring physics tuned to feel decisive without
  // bouncing.
  const iconWrapVariants = {
    rest: {
      scale: 1,
      rotate: 0,
      backgroundColor: "rgba(167, 185, 169, 0.18)", // sage-tinted
    },
    hover: {
      scale: reduce ? 1 : 1.08,
      rotate: reduce ? 0 : 3,
      backgroundColor: "rgba(200, 163, 92, 0.18)", // gold-tinted
    },
  };

  // The icon glyph itself: stroke color sage to gold.
  const iconColorVariants = {
    rest: { color: "rgb(74, 96, 81)" },  // muted forest sage
    hover: { color: "rgb(200, 163, 92)" }, // maren-gold
  };

  return (
    <motion.div
      initial="rest"
      whileHover="hover"
      whileTap="hover"
      animate="rest"
      variants={cardVariants}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className={`relative rounded-2xl p-6 sm:p-7 ${
        highlight
          ? "border-2 border-maren-gold dark:border-maren-gold bg-maren-gold/8 dark:bg-maren-charcoal"
          : "border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal/70"
      }`}
      style={{ willChange: "transform" }}
    >
      {highlight && (
        // Non-superlative descriptor per legal-review-20260422.md §H1.
        <span className="absolute -top-3 left-6 rounded-full bg-maren-gold px-3 py-0.5 text-[10px] font-bold text-maren-charcoal uppercase tracking-wide">
          {highlightLabel}
        </span>
      )}

      {/* Gold ring on hover. Pointer-events none so it never intercepts
          clicks. Inset slightly so the ring sits flush with the card's
          existing border radius. */}
      <motion.span
        aria-hidden
        variants={ringVariants}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-maren-gold/35 dark:ring-maren-gold/40"
      />

      <div className="relative flex items-start gap-4">
        <motion.div
          variants={iconWrapVariants}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="shrink-0 rounded-xl p-2.5"
        >
          <motion.div
            variants={iconColorVariants}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </motion.div>
        </motion.div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-maren-charcoal dark:text-maren-stone">
            {title}
          </h3>
          <p className="mt-2 text-sm text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed">
            {pitch}
          </p>
          <Link
            href={href}
            onClick={onCtaClick}
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold transition text-maren-green dark:text-maren-gold hover:text-maren-green/80 dark:hover:text-maren-gold/80"
          >
            {cta}
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
